/**
 * Settings → IA: behavior only.
 *
 * Provider connections (API keys, OAuth, "Probar conexión") live exclusively
 * in the Proveedores accordion — this section used to carry a second OpenCode
 * Go key field that could store a credential but never verify it. What is
 * configured here is the global route plus, per AI task, which provider and
 * model run it, with how much effort, in what tone and under which prompt.
 */

import React, { useState } from 'react';
import {
  AI_EFFORTS,
  AI_TASK_IDS,
  type AiEffort,
  type AiGenerationSettings,
  type AiTaskId,
  type AiTaskOverride,
  type SettingsAiProvider,
  type UserSettingsPublic,
  type UserSettingsUpdate,
} from '@zunelo/shared';
import { AI_PROVIDER_LABELS, EFFORT_LABELS } from './aiSettingsLabels';
import { openSettingsSection } from './settingsDeepLink';
import { SettingsAiTaskRow } from './SettingsAiTaskRow';
import { SettingsAiUsageBlock } from './SettingsAiUsageBlock';
import { hintStyle, inputStyle, labelStyle, sectionStyle, fieldGridStyle } from './settingsStyles';
import { useAiModelCatalog } from './useAiModelCatalog';

export interface SettingsAiSectionProps {
  settings: UserSettingsPublic;
  saving: boolean;
  aiDraft: AiGenerationSettings;
  onAiDraft: (value: AiGenerationSettings) => void;
  onAutoSaveAi: (value: AiGenerationSettings) => void;
  onSavePatch: (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
  ) => Promise<void>;
}

export const AI_PROVIDER_ORDER: readonly SettingsAiProvider[] = [
  'opencode-go',
  'gemini',
  'openai-codex',
];

const linkButtonStyle: React.CSSProperties = {
  border: 0,
  background: 'transparent',
  padding: 0,
  color: 'var(--z-accent)',
  cursor: 'pointer',
};

/** How many task model pins a global provider change will drop. */
export function countDroppedTaskModels(tasks: AiGenerationSettings['tasks']): number {
  return Object.values(tasks ?? {}).filter((override) => override.model && !override.provider)
    .length;
}

/**
 * Strip the model from every task that has no provider of its own, keeping
 * each task's other choices (effort, tone, prompt) intact.
 */
export function dropInheritedTaskModels(
  tasks: AiGenerationSettings['tasks'],
): AiGenerationSettings['tasks'] {
  if (!tasks) return tasks;
  const next: NonNullable<AiGenerationSettings['tasks']> = {};
  for (const [task, override] of Object.entries(tasks) as Array<[AiTaskId, AiTaskOverride]>) {
    if (override.provider) {
      next[task] = override;
      continue;
    }
    const rest: AiTaskOverride = { ...override };
    delete rest.model;
    if (Object.keys(rest).length) next[task] = rest;
  }
  return next;
}

/** Providers with a stored credential — the only ones a task may be routed to. */
export function configuredAiProviders(settings: UserSettingsPublic): SettingsAiProvider[] {
  const configured: SettingsAiProvider[] = [];
  if (settings.secrets.opencodeGoApiKey?.configured) configured.push('opencode-go');
  if (settings.secrets.geminiApiKey?.configured) configured.push('gemini');
  if (settings.secrets.openAiCodexOAuth?.configured || settings.secrets.openAiApiKey?.configured) {
    configured.push('openai-codex');
  }
  return configured;
}

export const SettingsAiSection: React.FC<SettingsAiSectionProps> = ({
  settings,
  saving,
  aiDraft,
  onAiDraft,
  onAutoSaveAi,
}) => {
  const catalog = useAiModelCatalog();
  const [providerSwitchNotice, setProviderSwitchNotice] = useState<string | null>(null);
  const configured = configuredAiProviders(settings);
  const unconnected = AI_PROVIDER_ORDER.filter((provider) => !configured.includes(provider));

  const applyGlobal = (changes: Partial<AiGenerationSettings>) => {
    const next: AiGenerationSettings = { ...aiDraft, ...changes };
    // The notice reports one specific switch; any later change makes it stale.
    if (!changes.provider) setProviderSwitchNotice(null);
    if (changes.provider && changes.provider !== aiDraft.provider) {
      // The global model belonged to the previous provider's roster and
      // governs every task that inherits the global route, so it is re-pointed
      // at the new provider's own first model — and task-level model pins,
      // chosen from that same old roster, are dropped.
      next.model = catalog.defaultModelFor(changes.provider);
      next.tasks = dropInheritedTaskModels(aiDraft.tasks);
      const dropped = countDroppedTaskModels(aiDraft.tasks);
      setProviderSwitchNotice(
        dropped
          ? `Modelo global ajustado a ${next.model}; se quitó el modelo fijado en ${dropped} ${
              dropped === 1 ? 'tarea' : 'tareas'
            }.`
          : `Modelo global ajustado a ${next.model}.`,
      );
    }
    onAiDraft(next);
    onAutoSaveAi(next);
  };

  const applyTaskOverride = (task: AiTaskId, override: AiTaskOverride) => {
    const tasks = { ...(aiDraft.tasks ?? {}) };
    if (Object.keys(override).length) tasks[task] = override;
    else delete tasks[task];
    onAiDraft({ ...aiDraft, tasks });
  };

  const saveTaskOverride = (task: AiTaskId, override: AiTaskOverride) => {
    const tasks = { ...(aiDraft.tasks ?? {}) };
    if (Object.keys(override).length) tasks[task] = override;
    else delete tasks[task];
    onAutoSaveAi({ ...aiDraft, tasks });
  };

  return (
    <section style={{ ...sectionStyle, borderBottom: 'none' }} data-testid="settings-ai-section">
      <div style={labelStyle}>Generación con IA</div>
      <p style={hintStyle}>
        Elige qué proveedor y modelo hace cada tarea. Las credenciales se conectan en{' '}
        <button
          type="button"
          data-testid="settings-ai-providers-link"
          onClick={() => openSettingsSection('providers')}
          style={linkButtonStyle}
        >
          Proveedores
        </button>
        . Los cambios se guardan al hacerlos.
      </p>

      <div className="z-settings-field-grid" style={fieldGridStyle}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={labelStyle}>Proveedor global</span>
          <select
            data-testid="settings-ai-provider"
            // While the roster is still loading, a switch would pin the
            // degraded static model — the retired-model trap this replaced.
            disabled={catalog.loading}
            value={aiDraft.provider}
            onChange={(event) =>
              applyGlobal({ provider: event.target.value as SettingsAiProvider })
            }
            style={inputStyle}
          >
            {AI_PROVIDER_ORDER.map((provider) => (
              <option key={provider} value={provider} disabled={!configured.includes(provider)}>
                {AI_PROVIDER_LABELS[provider]}
                {configured.includes(provider) ? '' : ' · sin conectar'}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={labelStyle}>Modelo global</span>
          <select
            data-testid="settings-ai-model"
            value={aiDraft.model}
            onChange={(event) => applyGlobal({ model: event.target.value })}
            style={inputStyle}
          >
            {catalog.optionsFor(aiDraft.provider, aiDraft.model).map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={labelStyle}>Esfuerzo global</span>
          <select
            data-testid="settings-ai-effort"
            value={aiDraft.effort ?? ''}
            onChange={(event) =>
              applyGlobal({ effort: (event.target.value || undefined) as AiEffort | undefined })
            }
            style={inputStyle}
          >
            <option value="">Por defecto de cada tarea</option>
            {AI_EFFORTS.map((effort) => (
              <option key={effort} value={effort}>
                {EFFORT_LABELS[effort]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!catalog.loading && catalog.catalog[aiDraft.provider]?.status === 'error' ? (
        <p data-testid="settings-ai-catalog-degraded" style={hintStyle}>
          No se pudo leer la lista de modelos de {AI_PROVIDER_LABELS[aiDraft.provider]}; se muestran
          los conocidos.
        </p>
      ) : null}
      {unconnected.length ? (
        <p data-testid="settings-ai-unconnected" style={hintStyle}>
          Sin conectar:{' '}
          {unconnected.map((provider, index) => (
            <React.Fragment key={provider}>
              {index > 0 ? ', ' : ''}
              <button
                type="button"
                data-testid={`settings-ai-connect-${provider}`}
                onClick={() => openSettingsSection(provider)}
                style={linkButtonStyle}
              >
                {AI_PROVIDER_LABELS[provider]}
              </button>
            </React.Fragment>
          ))}
          .
        </p>
      ) : null}

      {providerSwitchNotice ? (
        <p
          data-testid="settings-ai-provider-switch-notice"
          role="status"
          aria-live="polite"
          style={hintStyle}
        >
          {providerSwitchNotice}
        </p>
      ) : null}

      {catalog.loading ? (
        <p data-testid="settings-ai-catalog-loading" style={hintStyle}>
          Consultando los modelos disponibles…
        </p>
      ) : null}

      <div style={labelStyle}>Tareas</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {AI_TASK_IDS.map((task) => (
          <SettingsAiTaskRow
            key={task}
            task={task}
            aiDraft={aiDraft}
            catalog={catalog}
            configuredProviders={configured}
            saving={saving}
            onOverride={applyTaskOverride}
            onSaveOverride={saveTaskOverride}
          />
        ))}
      </div>

      <SettingsAiUsageBlock configuredProviders={configured} />
    </section>
  );
};
